import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { useApi } from "../lib/api";

const Shell = ({ icon, title, text, testId, children }) => (
  <div data-testid={testId} className="mx-auto max-w-lg rounded-2xl border border-border/70 bg-card p-8 text-center">
    <div className="flex justify-center">{icon}</div>
    <h1 className="mt-5 font-heading text-4xl font-black uppercase tracking-tight">{title}</h1>
    <p className="mt-3 text-sm text-muted-foreground">{text}</p>
    <div className="mt-8 flex justify-center gap-3">{children}</div>
  </div>
);

const btn = "rounded-full bg-amber-500 px-6 py-3 text-sm font-bold text-[#0B0F17] hover:bg-amber-400";

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const api = useApi();
  const qc = useQueryClient();
  const [state, setState] = useState("checking");

  useEffect(() => {
    let tries = 0;
    let timer;
    const poll = async () => {
      try {
        const r = await api("get", `/payments/status/${sessionId}`);
        if (r.payment_status === "paid" && r.premium) {
          qc.invalidateQueries({ queryKey: ["subscription"] });
          return setState("paid");
        }
        if (r.payment_status === "expired" || r.payment_status === "failed") return setState("failed");
      } catch {}
      if (++tries >= 15) return setState("timeout");
      timer = setTimeout(poll, 2000);
    };
    if (sessionId) poll(); else setState("failed");
    return () => clearTimeout(timer);
  }, [api, qc, sessionId]);

  if (state === "checking")
    return <Shell testId="payment-checking" icon={<Loader2 className="h-12 w-12 animate-spin text-amber-500" />} title="Confirming payment" text="Hang on while we confirm your subscription with Stripe." />;
  if (state === "paid")
    return (
      <Shell testId="payment-success" icon={<CheckCircle2 className="h-12 w-12 text-emerald-400" />} title="You're Premium" text="Thanks for upgrading. Your ShiftMate Premium subscription is active.">
        <Link to="/premium" data-testid="payment-success-premium-link" className={btn}>View my plan</Link>
      </Shell>
    );
  return (
    <Shell testId="payment-not-confirmed" icon={<XCircle className="h-12 w-12 text-red-400" />} title="Not confirmed yet"
      text={state === "timeout" ? "We couldn't confirm your payment yet. If you were charged, Premium will switch on within a few minutes." : "This payment did not go through. You have not been charged."}>
      <Link to="/premium" data-testid="payment-back-premium" className={btn}>Back to Premium</Link>
    </Shell>
  );
}

export function PaymentCancel() {
  return (
    <Shell testId="payment-cancel" icon={<XCircle className="h-12 w-12 text-muted-foreground" />} title="Checkout cancelled" text="No payment was taken. You can upgrade any time.">
      <Link to="/premium" data-testid="payment-cancel-back" className={btn}>Back to Premium</Link>
    </Shell>
  );
}
